import React, {useEffect, useMemo, useState, useRef, useCallback} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {
  View,
  Platform,
  BackHandler,
  ScrollView,
  TouchableOpacity,
  Text,
  Alert,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  FadeOut,
  FadeOutDown,
  ZoomIn,
  ZoomInEasyDown,
  ZoomInEasyUp,
  ZoomOut,
} from 'react-native-reanimated';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Theme} from '../../../theme/lightTheme';
import ChatHistoryItem from '../chatHistoryItem';
import {RootState, store} from '../../../redux/store';
import {useSelector, useDispatch} from 'react-redux';
import SkeletonPlaceholder from '../../skeletonPlaceholder';
import Icon from '../../icon';
import {closeSettingsModal} from '../../../connected-components/settingsModal/actions';
import {ThreadItemType} from '../../../interfaces/api/qna';
import TextInput from '../../textInput';
import {tr} from '../../../translations';
import TextGeneric from '../../textGeneric';
import {BottomSheetScrollView} from '@gorhom/bottom-sheet';
import {navigationRef} from '../../../navigation';
import {ChatItemType} from '../../../interfaces/reducers/chat';
import {
  deleteChat,
  getMessagesHistory,
  toggleChatFavorite,
  shareChat,
  getChatHistory,
  getFavoriteChats,
} from '../../../screens/content/chat/actions';
import {
  removeChat,
  selectChat,
  setMessages,
  toggleChatFavoriteAction,
  setChats,
  appendChats,
  setFavChats,
  appendFavChats,
  resetChatsFavs,
  clearSelectedChat,
  setActiveChatHistoryTab,
  setSelectedChatFavorite,
} from '../../../screens/content/chat/reducer';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {exportChatToPdf} from '../../../utils/pdf';
import {ToastType, modalTypes} from '../../../utils/enums';
import {hdp, wdp} from '../../../utils/responsive';
import screens from '../../../config/screens';

// Styled Components
const Container = styled.View<{
  bottomInset: number;
  paddingTop: number;
  isWeb?: boolean;
}>`
  position: ${Platform.OS === 'web' ? 'relative' : 'absolute'};
  width: 100%;
  height: ${({isWeb}) => (isWeb ? '100%' : '100%')};
  background-color: ${({theme}) => theme.screenBackground};
  padding-bottom: ${({bottomInset}) => bottomInset}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
  elevation: 8;
  z-index: 1000;
  left: 0;
`;

const SearchInputContainer = styled.View<{
  paddingHorizontal: number;
  marginBottom: number;
}>`
  width: 100%;
  padding-horizontal: ${({paddingHorizontal}) => paddingHorizontal}px;
  margin-bottom: ${({marginBottom, theme}) => marginBottom + theme.text.s55}px;
`;

const SearchInput = styled(TextInput)`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  background-color: ${({theme}) => theme.text.dark + '11'};
  ${Platform.OS === 'web' ? '' : ''}
`;

const NewChatButton = styled.TouchableOpacity<{paddingHorizontal: number}>`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: ${({paddingHorizontal}) => paddingHorizontal}px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.primary + '22'};
  width: 96%;
  align-self: center;
  margin-bottom: ${({theme}) => theme.text.s12}px;
`;

const NewIconContainer = styled.View`
  width: ${({theme}) => theme.text.s1}px;
  height: ${({theme}) => theme.text.s1}px;
  background-color: ${({theme}) => theme.primary + '22'};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
`;

// Back icon without background
const BackIconContainer = styled.View`
  width: ${({theme}) => theme.text.s1}px;
  height: ${({theme}) => theme.text.s1}px;
  border-radius: 100px;
  align-items: center;
  justify-content: center;
`;

const NewChatText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  margin-left: ${({theme}) => theme.text.s8}px;
`;

const DateSeparator = styled(TextGeneric)<{padding: number}>`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme}) => theme.text.grey};
  padding: ${({padding}) => padding}px;
  opacity: 0.7;
`;

const EmptyContainer = styled.View<{padding: number}>`
  align-items: center;
  justify-content: center;
  padding: ${({padding}) => padding}px 0;
`;

const EmptyText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
`;

const SkeletonContainer = styled.View`
  width: 100%;
`;

const Separator = styled.View`
  width: 100%;
  height: 1px;
  align-self: center;
  background-color: ${({theme}) => theme.text.grey + '44'};
  margin-vertical: ${({theme}) => theme.text.s7}px;
`;

const TabContainer = styled.View<{paddingHorizontal: number}>`
  flex-direction: row;
  margin-horizontal: ${({paddingHorizontal}) => paddingHorizontal}px;
  margin-bottom: ${({theme}) => theme.text.s55}px;
  margin-top: ${({theme}) => theme.text.s12}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.text.dark + '22'};
`;

const TabButton = styled.TouchableOpacity<{isActive: boolean}>`
  flex: 1;
  padding-vertical: ${({theme}) => theme.text.s8}px;
  align-items: center;
  border-bottom-width: ${({isActive}) => (isActive ? 2 : 0)}px;
  border-bottom-color: ${({theme}) => theme.primary};
`;

const TabText = styled(TextGeneric)<{isActive: boolean}>`
  font-size: ${({theme}) => theme.text.s8}px;
  font-weight: ${({isActive}) => (isActive ? '700' : '500')};
  color: ${({theme, isActive}) =>
    isActive ? theme.text.dark : theme.text.dark + '99'};
`;

const LoadMoreButton = styled.TouchableOpacity<{isLoading: boolean}>`
  padding: ${({theme}) => theme.text.s55}px;
  margin: ${({theme}) => theme.text.s55}px;
  background-color: ${({theme}) => theme.primary + '22'};
  border-radius: 10px;
  align-items: center;
  opacity: ${({isLoading}) => (isLoading ? 0.6 : 1)};
`;

const LoadMoreText = styled(TextGeneric)`
  color: ${({theme}) => theme.primary};
  font-size: ${({theme}) => theme.text.s8}px;
  font-weight: 600;
`;

// Inline options menu (animated)
const RowContainer = styled.View`
  position: relative;
`;

const OptionsMenu = styled(Animated.View)`
  position: absolute;
  right: 8px;
  top: 36px;
  background-color: ${({theme}) => theme.screenBackground + 'EE'};
  border: 1px solid ${({theme}) => theme.secondary + '44'};
  border-radius: 10px;
  min-width: 180px;
  padding-vertical: 6px;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.25;
  shadow-radius: 10px;
  elevation: 8;
  z-index: 10000;
`;

const OptionItem = styled.TouchableOpacity`
  padding-vertical: 12px;
  padding-horizontal: 16px;
  flex-direction: row;
  align-items: center;
`;

const Backdrop = styled(Animated.View)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

// Helper functions for date grouping
function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  );
}

interface ChatHistoryProps {
  isQNA: boolean;
  onCreateNewChatPressed?: () => void;
  onChatItemPressed: (chat: ThreadItemType | ChatItemType) => void;
  selectedChatId?: string;
  tab_id: string; // Required prop for tab_id
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
                                                   isQNA,
                                                   selectedChatId,
                                                   onCreateNewChatPressed,
                                                   onChatItemPressed,
                                                   tab_id,
                                                 }) => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {wdp, hdp, getByScreenSize, isLargeScreen} = useResponsiveDimensions();
  const dispatch = useDispatch();
  const [localSelectedId, setLocalSelectedId] = useState<
    string | number | undefined
  >(selectedChatId);

  // Get chat data from Redux
  const {chats, favChats, messages, activeChatHistoryTab, currentChatType} = useSelector(
    (state: RootState) => state.chat,
  );

  const [activeTab, setActiveTab] = useState<'all' | 'favorite'>(activeChatHistoryTab);
  const [search, setSearch] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Debounce timer ref
  const searchTimeoutRef = useRef<any>(null);
  // Options menu state
  const [optionsChatId, setOptionsChatId] = useState<string | number | null>(
    null,
  );

  // Add a state to track if we're in the initial loading phase
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Pagination states for All Chats
  const [allPage, setAllPage] = useState(1);
  const [allHasMore, setAllHasMore] = useState(true);
  const [allLoading, setAllLoading] = useState(false);

  // Pagination states for Favorite Chats
  const [favPage, setFavPage] = useState(1);
  const [favHasMore, setFavHasMore] = useState(true);
  const [favLoading, setFavLoading] = useState(false);
  const [favTotal, setFavTotal] = useState(0);

  const prevTabIdRef = useRef<string | null>(null);

  // Load chats on component mount and when tab_id changes
  useEffect(() => {
    if (!tab_id) return;
    if (prevTabIdRef.current === tab_id) return;
    prevTabIdRef.current = tab_id;
    setFavPage(1);
    setSearch('');
    store.dispatch(resetChatsFavs());
    if (activeChatHistoryTab === 'all') {
      loadChats();
    } else {
      loadFavoriteChats();
    }
  }, [tab_id, activeChatHistoryTab, loadChats]);

  // Load chats function
  const PAGE_SIZE = 50;
  const loadChats = useCallback(async (searchQuery?: string) => {
    if (allLoading || !tab_id) return;

    setAllLoading(true);
    try {
      const res = await getChatHistory(
        0,
        PAGE_SIZE,
        searchQuery || '',
        0,
        tab_id,
      );
      if (res.success) {
        const newChats = res.chats;
        const existing = store.getState().chat.chats || [];
        const selectedId = store.getState().chat.selectedChatId;
        const newIds = new Set(
          newChats.map(chat => (chat.model_id || chat.id)?.toString()),
        );
        const preserved = existing.filter(chat => {
          const id = (chat.model_id || chat.id)?.toString();
          if (!id || newIds.has(id)) return false;
          const isSelected =
            selectedId && id === selectedId.toString();
          const isLocal = (chat as any).is_local;
          return isSelected || isLocal;
        });
        const mergedChats = [...preserved, ...newChats];
        if (newChats.length < PAGE_SIZE) {
          setAllHasMore(false);
        } else {
          setAllHasMore(true);
        }
        dispatch(setChats(mergedChats));
        setHasMore(newChats.length === PAGE_SIZE);
        setCurrentPage(1);
      }
    } catch (err) {
    } finally {
      setAllLoading(false);
      setIsInitialLoading(false);
    }
  }, [allLoading, tab_id]);

  // Load favorite chats
  const loadFavoriteChats = async () => {
    if (favLoading) return;

    setFavLoading(true);
    try {
      const res = await getFavoriteChats(1, PAGE_SIZE, currentChatType, tab_id);
      if (res.success) {
        const data = [...(res.chats || [])];
        data.forEach(item => item.is_favorite = true);
        const newChats = data;
        setFavHasMore(res.hasMore);
        setFavTotal(res.total);
        dispatch(setFavChats(newChats));
      }
    } catch (err) {
    } finally {
      setFavLoading(false);
    }
  };

  // Load more chats for pagination
  const loadMoreChats = async () => {
    if (activeTab === 'all' && allHasMore && !allLoading) {
      if (!tab_id) return;
      setAllPage(prev => prev + 1);
      const start = allPage * PAGE_SIZE;
      try {
        const res = await getChatHistory(start, PAGE_SIZE, search, 0, tab_id);
        if (res.success) {
          const newChats = res.chats;
          if (newChats.length < PAGE_SIZE) {
            setAllHasMore(false);
          }
          dispatch(appendChats(newChats));
        }
      } catch (err) {
      }
    } else if (activeTab === 'favorite' && favHasMore && !favLoading) {
      const nextPage = favPage + 1;
      setFavPage(nextPage);
      try {
        const res = await getFavoriteChats(nextPage, PAGE_SIZE, currentChatType, tab_id);
        if (res.success) {
          const newChats = res.chats;
          setFavHasMore(res.hasMore);
          setFavTotal(res.total);
          dispatch(appendFavChats(newChats));
        }
      } catch (err) {
      }
    }
  };

  // Handle back press from detail screen
  const handleBackPress = () => {
    if (search) {
      setSearch('');
      loadChats(); // Reload chats without search
      return true;
    }
    return false;
  };

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  useEffect(() => {
    return () => {
      setSearch('');
    };
  }, []);

  useEffect(() => {
    setLocalSelectedId(selectedChatId);
  }, [selectedChatId]);

  // Group chats by date (similar to the React JS version)
  const groupChatsByDate = (chats: (ThreadItemType | ChatItemType)[]) => {
    // Sort chats by date in descending order (newest first)
    const sortedChats = [...chats].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return sortedChats.reduce(
      (groups: {[key: string]: (ThreadItemType | ChatItemType)[]}, chat) => {
        const chatDate = new Date(chat.created_at);
        let groupName = 'Earlier';
        if (isToday(chatDate)) {
          groupName = 'Today';
        } else if (isYesterday(chatDate)) {
          groupName = 'Yesterday';
        }
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(chat);
        return groups;
      },
      {},
    );
  };

  // Determine current list based on activeTab
  const currentChats = activeTab === 'all' ? chats : favChats;
  const currentLoading = activeTab === 'all' ? allLoading : favLoading;
  const currentHasMore = activeTab === 'all' ? allHasMore : favHasMore;

  // Grouped chats by date
  const groupedChats = useMemo(
    () => groupChatsByDate(currentChats),
    [currentChats],
  );

  const handleNewChat = () => {
    onCreateNewChatPressed?.();
    if (Platform.OS !== 'web') {
      closeSettingsModal();
    }
  };

  const handleChatItemPress = (chat: ThreadItemType | ChatItemType) => {
    setLocalSelectedId(chat.model_id || chat.id);
    onChatItemPressed(chat);
    setOptionsChatId(null);
    if (Platform.OS !== 'web') {
      closeSettingsModal();
    }
  };

  // Handle search input changes
  const handleSearchChange = (text: string) => {
    setSearch(text);
    // Debounce search to avoid too many API calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(text);
    }, 300);
  };

  // Handle search
  const handleSearch = async (searchQuery: string) => {
    try {
      setIsSearching(true);
      setCurrentPage(1);
      setHasMore(true);
      setAllHasMore(true);

      // Load chat history with search
      await loadChats(searchQuery);
    } catch (error) {
    } finally {
      setIsSearching(false);
    }
  };

  // Toggle favorite status using Redux
  const handleToggleFavorite = async (chat: ChatItemType) => {
    const newFavoriteValue = !chat.is_favorite ? true : false;
    try {
      const res = await toggleChatFavorite(
        Number(chat?.favorite_id || chat.id),
        newFavoriteValue,
        tab_id,
      );
      if (res) {
        dispatch(
          toggleChatFavoriteAction({
            id: chat.favorite_id || chat.id,
            isFavorite: newFavoriteValue,
            favorite_id: res.content?.model?.favorite_id || null,
            chatId: chat.id,
          }),
        );
      }
    } catch (err) {
    } finally {
      setOptionsChatId(null);
    }
  };

  // Delete chat
  const onChatDeleted = (chatId: number) => {
    openQuestionModal({
      customHeaderComponent: () => (
        <View
          style={{
            width: '100%',
            alignItems: 'center',
            paddingBottom: theme.text.s1,
          }}>
          <Icon
            name="delete"
            size={theme.text.s1 * 2}
            color={theme.text.error}
            style={{paddingBottom: '5%'}}
          />
        </View>
      ),
      title: tr('chat.deleteChatTitle'),
      message: tr('chat.deleteChatMessage'),
      type: modalTypes.question,
      onConfirm: async () => {
        try {
          const response = await deleteChat(chatId);
          if (response) {
            dispatch(removeChat(chatId));
            store.dispatch(setMessages({messages: []}));
            store.dispatch(selectChat(''));
            showGlobalToast({
              message: tr('chat.deleteChatSuccess'),
              type: ToastType.SUCCESS,
            });
          }
        } finally {
          setOptionsChatId(null);
        }
      },
      confirmText: tr('app.yes'),
      rejectText: tr('app.cancel'),
    });
  };

  const exportChat = async (id: number) => {
    try {
      if (selectedChatId && +selectedChatId === +id) {
        await exportChatToPdf([...(messages || [])].reverse());
        return;
      }

      const res = await getMessagesHistory(id);
      if (!res.messages.length) {
        showGlobalToast({
          message: 'No messages available for export.',
          type: ToastType.ERROR,
        });
        return;
      }

      await exportChatToPdf([...(res.messages || [])].reverse());
    } catch (ex) {
      showGlobalToast({
        message:
          'An error occurred while exporting the chat. Please try again later.',
        type: ToastType.ERROR,
      });
    } finally {
      setOptionsChatId(null);
    }
  };

  // Web/inline options menu open
  const showChatOptions = (chat: ChatItemType) => {
    if (Platform.OS === 'web') {
      setOptionsChatId(prev => (prev === chat.id ? null : chat.id));
      return;
    }
    Alert.alert(chat.title || 'Chat Options', 'Choose an action', [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text:
          chat.is_favorite ? 'Remove from Favorites' : 'Add to Favorites',
        onPress: () => handleToggleFavorite(chat),
      },
      {
        text: 'Share',
        onPress: () => shareChat(chat.id?.toString() || ''),
      },
      {
        text: 'Export',
        onPress: () => exportChat(Number(chat.id)),
      },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => onChatDeleted((chat.model_id || chat.id)),
      },
    ]);
  };

  // Handle tab switching
  const handleTabSwitch = (tab: 'all' | 'favorite') => {
    setActiveTab(tab);
    dispatch(setActiveChatHistoryTab(tab));
    setCurrentPage(1);
    setHasMore(true);
    if (tab === 'favorite') {
      loadFavoriteChats();
    } else {
      if (chats.length === 0) {
        loadChats();
      }
    }
  };

  // Refresh chats
  const refreshChats = async () => {
    if (activeTab === 'all') {
      setAllPage(1);
      setAllHasMore(true);
      await loadChats(search);
    } else {
      setFavPage(1);
      setFavHasMore(true);
      setFavTotal(0);
      await loadFavoriteChats();
    }
  };

  const renderSkeletonItems = () => {
    return (
      <SkeletonContainer>
        {[0, 1, 2, 4, 5].map(i => (
          <SkeletonPlaceholder
            key={i?.toString()}
            width={'100%'}
            height={theme.text.s1 * 1.6}
            borderRadius={8}
            marginBottom={theme.text.s12}
          />
        ))}
      </SkeletonContainer>
    );
  };

  const renderEmptyList = () => {
    return (
      <EmptyContainer padding={hdp(5)}>
        <EmptyText semiBold>{tr('chat.emptyHistory')}</EmptyText>
      </EmptyContainer>
    );
  };

  // Enhanced chat item with better functionality
  const renderChatItem = (
    chat: ThreadItemType | ChatItemType,
    groupName: string,
    idx: number,
  ) => {
    const isSelected =
      localSelectedId !== undefined &&
      chat.id !== undefined &&
      (chat.model_id || chat.id)?.toString() === localSelectedId?.toString();

    const isFav = 'is_favorite' in chat && chat.is_favorite;

    return (
      <RowContainer style={{zIndex: chats?.length - idx}} key={chat.id}>
        <TouchableOpacity
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: theme.text.s8,
            marginVertical: 2,
            borderRadius: 8,
            backgroundColor: isSelected ? theme.primary + '22' : 'transparent',
          }}
          onPress={() => handleChatItemPress(chat)}
          activeOpacity={0.7}>
          {/* Chat Icon */}
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme.primary + '22',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: theme.text.s4,
            }}>
            <Icon
              name={isFav ? 'star' : 'chat'}
              size={20}
              color={theme.primary}
            />
          </View>

          {/* Chat Info */}
          <View style={{flex: 1}}>
            <Text
              style={{
                fontSize: theme.text.s8,
                fontWeight: isSelected ? '600' : '500',
                color: theme.text.dark,
                marginBottom: 2,
              }}>
              {chat.title || 'Untitled'}
            </Text>
            {!!chat.created_at && <Text
              style={{
                fontSize: theme.text.s10,
                color: theme.text.grey,
              }}>
              {new Date(chat.created_at).toLocaleDateString()}
            </Text>}
          </View>

          {/* More menu */}
          <View style={{flexDirection: 'row', gap: 8}}>
            <TouchableOpacity
              style={{
                padding: 8,
                borderRadius: 16,
                backgroundColor: 'transparent',
              }}
              onPress={() => showChatOptions(chat as ChatItemType)}>
              <Icon name={'more'} size={16} color={theme.text.grey} />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>

        {/* Inline options menu for web with animation */}
        {Platform.OS === 'web' &&
          optionsChatId?.toString() === chat.id?.toString() && (
            <OptionsMenu entering={FadeInUp} exiting={FadeOutDown}>
              <OptionItem
                onPress={() => handleToggleFavorite(chat as ChatItemType)}>
                <Icon
                  name={isFav ? 'star' : 'starOutline'}
                  size={theme.text.s8}
                  color={theme.primary}
                />
                <Text
                  style={{
                    marginLeft: 10,
                    color: theme.text.dark,
                    fontWeight: '600',
                    fontSize: theme.text.s8,
                  }}>
                  {isFav ? 'Remove from Favorites' : 'Add to Favorites'}
                </Text>
              </OptionItem>
              <OptionItem onPress={() => shareChat(chat.id?.toString() || '')}>
                <Icon
                  name={'share'}
                  size={theme.text.s8}
                  color={theme.primary}
                />
                <Text
                  style={{
                    marginLeft: 10,
                    color: theme.text.dark,
                    fontWeight: '600',
                    fontSize: theme.text.s8,
                  }}>
                  Share
                </Text>
              </OptionItem>
              <OptionItem onPress={() => exportChat(Number(chat.id))}>
                <Icon
                  name={'download'}
                  size={theme.text.s8}
                  color={theme.primary}
                />
                <Text
                  style={{
                    marginLeft: 10,
                    color: theme.text.dark,
                    fontWeight: '600',
                    fontSize: theme.text.s8,
                  }}>
                  Export
                </Text>
              </OptionItem>
              <OptionItem onPress={() => onChatDeleted(Number(chat.model_id ||chat.id))}>
                <Icon
                  name={'delete'}
                  size={theme.text.s8}
                  color={theme.text.error}
                />
                <Text
                  style={{
                    marginLeft: 10,
                    color: theme.text.error,
                    fontWeight: '600',
                    fontSize: theme.text.s8,
                  }}>
                  Delete
                </Text>
              </OptionItem>
            </OptionsMenu>
          )}
        {Platform.OS === 'web' && optionsChatId !== null && (
          <Backdrop
            entering={FadeIn.duration(120)}
            exiting={FadeOut.duration(120)}
            pointerEvents="auto"
            onStartShouldSetResponder={() => true}
            onResponderRelease={() => setOptionsChatId(null)}
          />
        )}
      </RowContainer>
    );
  };

  return (
    <Container
      bottomInset={bottomInset || 0}
      paddingTop={Platform.OS === 'ios' ? hdp(6) : hdp(2)}
      isWeb={Platform.OS === 'web'}>

      {isLargeScreen && (
        <NewChatButton
          paddingHorizontal={getByScreenSize(wdp(4), theme.text.s12)}
          onPress={() => {
            dispatch(clearSelectedChat());
            setLocalSelectedId(undefined);
            setTimeout(() => {
              navigationRef?.current?.navigate(screens.content.home);
            }, 200);
          }}>
          <BackIconContainer>
            <Icon name={'back'} size={18} color={theme.primary} />
          </BackIconContainer>
          <NewChatText semiBold>{tr('app.back')}</NewChatText>
        </NewChatButton>
      )}

      <SearchInputContainer
        paddingHorizontal={getByScreenSize(wdp(4), theme.text.s12)}
        marginBottom={hdp(1)}>
        <SearchInput
          hideFocusedEffect
          placeholder={tr('chat.search')}
          placeholderTextColor={theme.text.grey}
          leftIconName={'search'}
          showLeftIcon
          value={search}
          onChangeText={text => handleSearchChange(text)}
          style={isLargeScreen ? {height: 40} : undefined}
        />
      </SearchInputContainer>


      {onCreateNewChatPressed && (
        <NewChatButton
          paddingHorizontal={getByScreenSize(wdp(4), theme.text.s12)}
          onPress={handleNewChat}>
          <NewIconContainer>
            <Icon name={'plus'} size={18} color={theme.primary} />
          </NewIconContainer>
          <NewChatText semiBold>{tr('chat.newChat')}</NewChatText>
        </NewChatButton>
      )}

      {/* Tab Bar */}
      <TabContainer paddingHorizontal={getByScreenSize(wdp(4), theme.text.s12)}>
        <TabButton
          isActive={activeTab === 'all'}
          onPress={() => handleTabSwitch('all')}>
          <TabText isActive={activeTab === 'all'} semiBold>
            All Chats
          </TabText>
        </TabButton>

        <TabButton
          isActive={activeTab === 'favorite'}
          onPress={() => handleTabSwitch('favorite')}>
          <TabText isActive={activeTab === 'favorite'} semiBold>
            Favorites
          </TabText>
        </TabButton>
      </TabContainer>

      {Platform.OS === 'web' ? (
        <ScrollView
          style={{
            flex: 1,
            paddingHorizontal: getByScreenSize(wdp(4), theme.text.s12),
            minHeight: 400,
            height: '100%',
          }}
          contentContainerStyle={{
            paddingBottom: Math.max(bottomInset, hdp(2)),
            minHeight: 400,
          }}
          showsVerticalScrollIndicator={true}
          onScrollBeginDrag={() => setOptionsChatId(null)}>
          {(((activeTab === 'all' ? isInitialLoading : false) &&
              currentChats.length === 0) ||
            (currentLoading && currentChats.length === 0))
            ? renderSkeletonItems()
            : currentChats.length === 0
              ? renderEmptyList()
              : Object.entries(groupedChats).map(([date, chats], index) => (
                  <View
                    style={{
                      zIndex: Object.entries(groupedChats).length - index,
                    }}
                    key={date}>
                    {index !== 0 && <Separator />}
                    <DateSeparator padding={hdp(1)} semiBold>
                      {date}
                    </DateSeparator>
                    {chats.map((chat, idx) => renderChatItem(chat, date, idx))}
                  </View>
                ))}

          {/* Load More Button */}
          {currentHasMore && currentChats.length >= PAGE_SIZE && (
            <LoadMoreButton
              isLoading={currentLoading}
              onPress={loadMoreChats}
              disabled={currentLoading}>
              <LoadMoreText>
                {currentLoading ? 'Loading...' : 'Load More'}
              </LoadMoreText>
            </LoadMoreButton>
          )}
        </ScrollView>
      ) : (
        <BottomSheetScrollView
          style={{
            flex: 1,
            paddingHorizontal: wdp(4),
          }}
          contentContainerStyle={{
            paddingBottom: Math.max(bottomInset, hdp(2)),
          }}
          showsVerticalScrollIndicator={false}
          onScrollBeginDrag={() => setOptionsChatId(null)}>
          {(((activeTab === 'all' ? isInitialLoading : false) &&
              currentChats.length === 0) ||
            (currentLoading && currentChats.length === 0))
            ? renderSkeletonItems()
            : currentChats.length === 0
              ? renderEmptyList()
              : Object.entries(groupedChats).map(([date, chats], index) => (
                <View key={date}>
                  {index !== 0 && <Separator />}
                  <DateSeparator padding={hdp(1)} semiBold>
                    {date}
                  </DateSeparator>
                  {chats.map((chat, idx) => renderChatItem(chat, date, idx))}
                </View>
              ))}

          {/* Load More Button */}
          {currentHasMore && currentChats.length >= PAGE_SIZE && (
            <LoadMoreButton
              isLoading={currentLoading}
              onPress={loadMoreChats}
              disabled={currentLoading}>
              <LoadMoreText>
                {currentLoading ? 'Loading...' : 'Load More'}
              </LoadMoreText>
            </LoadMoreButton>
          )}
        </BottomSheetScrollView>
      )}
    </Container>
  );
};

export default ChatHistory;
